// src/components/common/ImageCropper.js
import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';

// Loads an image file into an <img> element we can draw to canvas
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

// Produces a 1000x1000 JPEG blob of the chosen circular/square area,
// stepping quality down until the file is guaranteed under 1 MB.
const getCroppedBlob = async (imageSrc, cropPixels) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const OUTPUT_SIZE = 1000;
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;

  // Draw the selected square region, scaled to 1000x1000
  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    OUTPUT_SIZE,
    OUTPUT_SIZE
  );

  const ONE_MB = 1024 * 1024;
  const qualitySteps = [0.9, 0.8, 0.7, 0.6, 0.5];

  const toBlob = (quality) =>
    new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));

  let blob = null;
  for (const q of qualitySteps) {
    blob = await toBlob(q);
    if (blob && blob.size <= ONE_MB) break;
  }

  return blob;
};

const ImageCropper = ({ imageSrc, onCancel, onCropComplete, language = 'mr' }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const t = language === 'mr'
    ? { title: 'फोटो निवडा', cancel: 'रद्द करा', save: 'सेव्ह करा', saving: 'सेव्ह होत आहे...' }
    : { title: 'Adjust Photo', cancel: 'Cancel', save: 'Save', saving: 'Saving...' };

  const onCropAreaChange = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels || processing) return;
    setProcessing(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);
      if (blob) {
        const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
        onCropComplete(file);
      } else {
        onCancel();
      }
    } catch (e) {
      onCancel();
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>{t.title}</div>

        <div style={cropAreaStyle}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropAreaChange}
          />
        </div>

        <div style={sliderWrapStyle}>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: '100%' }}
          />
        </div>

        <div style={btnRowStyle}>
          <button style={cancelBtnStyle} onClick={onCancel} disabled={processing}>
            {t.cancel}
          </button>
          <button style={saveBtnStyle} onClick={handleSave} disabled={processing}>
            {processing ? t.saving : t.save}
          </button>
        </div>
      </div>
    </div>
  );
};

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.85)',
  zIndex: 2000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
};

const modalStyle = {
  background: '#fff',
  borderRadius: '16px',
  width: '100%',
  maxWidth: '420px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle = {
  padding: '16px',
  fontSize: '17px',
  fontWeight: 700,
  color: '#1a1a1a',
  textAlign: 'center',
  borderBottom: '1px solid #eee',
};

const cropAreaStyle = {
  position: 'relative',
  width: '100%',
  height: '320px',
  background: '#333',
};

const sliderWrapStyle = {
  padding: '16px 20px 8px',
};

const btnRowStyle = {
  display: 'flex',
  gap: '10px',
  padding: '8px 16px 16px',
};

const cancelBtnStyle = {
  flex: 1,
  padding: '14px',
  background: '#f0f0f0',
  color: '#333',
  border: 'none',
  borderRadius: '10px',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
};

const saveBtnStyle = {
  flex: 1,
  padding: '14px',
  background: '#007fff',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
};

export default ImageCropper;